import React from 'react'

import Spinner from '../spinner'
import type { InvoicesSummary } from '../../../api'
import { useAnimatedInvoicesSummary } from '../../../hooks/useAnimatedInvoicesSummary'
import AnimatedSummaryValue from './AnimatedSummaryValue'

import './styles.scss'

type Props = {
  data: InvoicesSummary | null
  isLoading: boolean
  currencySymbol?: string
  topSection?: React.ReactNode
}

const SummaryCards: React.FC<Props> = ({ data, isLoading, currencySymbol, topSection }) => {
  const { display } = useAnimatedInvoicesSummary(data, isLoading)

  const renderCountLabel = (count: number) =>
    `${count} ${count === 1 ? 'invoice' : 'invoices'}`

  return (
    <div className="summary-cards">
      {topSection && <div className="summary-cards__top">{topSection}</div>}
      <div className="summary-cards__grid">
        <div className="summary-cards__card">
          <h6 className="summary-cards__card-title">Total invoiced</h6>
          <p className="help-tip-text">Sum of all invoice totals.</p>
          <div className="summary-cards__card-right">
            <div className="summary-cards__card-value">
              {isLoading && <Spinner />}
              {!isLoading && display != null && (
                <AnimatedSummaryValue
                  value={display.totalAmount}
                  mode="amount"
                  currencySymbol={currencySymbol}
                  className="summary-cards__amount"
                />
              )}
              {!isLoading && display == null && <span className="summary-cards__empty">—</span>}
            </div>
            {!isLoading && display != null && (
              <AnimatedSummaryValue
                value={display.invoiceCount}
                mode="count"
                className="summary-cards__card-meta"
                countLabel={renderCountLabel}
              />
            )}
          </div>
        </div>
        <div className="summary-cards__card summary-cards__card--paid">
          <h6 className="summary-cards__card-title">Invoices paid</h6>
          <p className="help-tip-text">Sum of invoices with status Paid.</p>
          <div className="summary-cards__card-right">
            <div className="summary-cards__card-value">
              {isLoading && <Spinner />}
              {!isLoading && display != null && (
                <AnimatedSummaryValue
                  value={display.paidAmount}
                  mode="amount"
                  currencySymbol={currencySymbol}
                  className="summary-cards__amount"
                />
              )}
              {!isLoading && display == null && <span className="summary-cards__empty">—</span>}
            </div>
            {!isLoading && display != null && (
              <AnimatedSummaryValue
                value={display.paidCount ?? 0}
                mode="count"
                className="summary-cards__card-meta"
                countLabel={renderCountLabel}
              />
            )}
          </div>
        </div>
        <div className="summary-cards__card summary-cards__card--due">
          <h6 className="summary-cards__card-title">Invoices due</h6>
          <p className="help-tip-text">Sum of invoices Open or Overdue.</p>
          <div className="summary-cards__card-right">
            <div className="summary-cards__card-value">
              {isLoading && <Spinner />}
              {!isLoading && display != null && (
                <AnimatedSummaryValue
                  value={display.dueAmount}
                  mode="amount"
                  currencySymbol={currencySymbol}
                  className="summary-cards__amount"
                />
              )}
              {!isLoading && display == null && <span className="summary-cards__empty">—</span>}
            </div>
            {!isLoading && display != null && (
              <AnimatedSummaryValue
                value={display.dueCount ?? 0}
                mode="count"
                className="summary-cards__card-meta"
                countLabel={renderCountLabel}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SummaryCards
